import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MdNotifications, MdCheckCircle, MdCancel, MdInfo, MdNotificationsActive, MdNotificationsOff, MdClose, MdSettings, MdDelete, MdThumbUp, MdThumbDown } from 'react-icons/md';
import { usePushSubscription } from '../hooks/usePushSubscription';
import NotificationPermissionPrompt from './NotificationPermissionPrompt';
import { getApiBase } from '../utils/getApiBase';

/**
 * Panel powiadomień - dropdown z listą powiadomień i zarządzaniem subskrypcją push
 */
export default function NotificationsPanel({ user, isOpen, onClose, onUnreadCountChange, onRefreshSchedule, onLoadNotificationsReady }) {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showPermissionPrompt, setShowPermissionPrompt] = useState(false);
    const [proposalResponses, setProposalResponses] = useState({}); // Śledzi udzielone odpowiedzi
    const [quotaError, setQuotaError] = useState(false);
    const [showAndroidTips, setShowAndroidTips] = useState(false);
    const panelRef = useRef(null);
    const pushSubscriptionRaw = usePushSubscription(user);
    const pushSubscription = useMemo(() => {
        if (pushSubscriptionRaw) return pushSubscriptionRaw;
        return {
            isSupported: false,
            isSubscribed: false,
            permission: 'default',
            isSubscribing: false,
            syncError: null,
            subscribe: async () => false,
            unsubscribe: async () => false,
        };
    }, [pushSubscriptionRaw]);
    const loadNotificationsRef = useRef(null); // Ref do funkcji loadNotifications dla Service Worker
    const clientInstanceId = useMemo(() => {
        if (typeof window === 'undefined') return '';
        try {
            return sessionStorage.getItem('planner:instanceId') || localStorage.getItem('planner:lastInstanceId') || '';
        } catch {
            return '';
        }
    }, []);

    // Kanoniczny identyfikator użytkownika (spójny z zapisem subskrypcji i wysyłką push)
    const resolvedUserId = useMemo(() => {
        if (!user) return '';
        return user.login || user.id || user.name || '';
    }, [user]);

    // Wszystkie możliwe identyfikatory (login, id, name) – żeby API znalazło powiadomienia zapisane pod dowolnym wariantem (desktop vs mobile)
    const allUserIds = useMemo(() => {
        if (!user) return [];
        return [user.login, user.id, user.name].filter((v) => v != null && String(v).trim() !== '');
    }, [user]);

    // Oblicz liczbę nieprzeczytanych powiadomień
    const unreadCount = notifications.filter(n => !n.read).length;

    // Powiadom parent o zmianie liczby nieprzeczytanych powiadomień i ustaw badge
    useEffect(() => {
        if (onUnreadCountChange) {
            onUnreadCountChange(unreadCount);
        }
        
        // Ustaw badge na ikonie aplikacji
        // Badge API działa w większości nowoczesnych przeglądarek, nawet bez pełnej instalacji PWA
        const isMobile = typeof navigator !== 'undefined' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        if (typeof navigator !== 'undefined' && 'setAppBadge' in navigator) {
            if (unreadCount > 0) {
                navigator.setAppBadge(unreadCount).then(() => {
                    console.log(`[NotificationsPanel] ✅ Badge ustawiony na ${unreadCount}`);
                    if (isMobile) {
                        console.log('[NotificationsPanel] 📱 Mobile: Badge ustawiony pomyślnie');
                    }
                }).catch(err => {
                    console.warn('[NotificationsPanel] ⚠️ Nie udało się ustawić badge:', err);
                    if (isMobile) {
                        console.error('[NotificationsPanel] 📱 Mobile: Błąd ustawiania badge:', err.message);
                        console.info('[NotificationsPanel] 📱 Mobile: Sprawdź czy aplikacja jest zainstalowana jako PWA');
                    }
                });
            } else {
                navigator.clearAppBadge().then(() => {
                    console.log('[NotificationsPanel] ✅ Badge wyczyszczony');
                }).catch(err => {
                    console.warn('[NotificationsPanel] ⚠️ Nie udało się wyczyścić badge:', err);
                });
            }
        } else {
            // Badge API nie jest wspierane
            if (isMobile) {
                console.warn('[NotificationsPanel] 📱 Mobile: Badge API nie jest wspierane w tej przeglądarce');
                console.info('[NotificationsPanel] 📱 Mobile: Wspierane w Chrome 84+, Edge 84+, Safari 16+');
            } else {
                console.log('[NotificationsPanel] ℹ️ Badge API nie jest wspierane w tym środowisku');
            }
        }
        
        // Sprawdź status service workera i subskrypcji push
        if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
            navigator.serviceWorker.ready.then(registration => {
                console.log('[NotificationsPanel] 📱 Service Worker status:', {
                    active: registration.active ? 'TAK' : 'NIE',
                    scope: registration.scope,
                    updateViaCache: registration.updateViaCache
                });
                
                // Sprawdź subskrypcję push
                if (registration.pushManager) {
                    registration.pushManager.getSubscription().then(sub => {
                        if (sub) {
                            console.log('[NotificationsPanel] 📱 Push subscription:', {
                                endpoint: sub.endpoint.substring(0, 50) + '...',
                                hasKeys: !!(sub.getKey('p256dh') && sub.getKey('auth'))
                            });
                        } else {
                            console.warn('[NotificationsPanel] 📱 ⚠️ Brak subskrypcji push - powiadomienia nie będą działać');
                            console.info('[NotificationsPanel] 📱 💡 Kliknij "Włącz powiadomienia" w panelu powiadomień');
                        }
                    }).catch(err => {
                        console.error('[NotificationsPanel] 📱 ❌ Błąd sprawdzania subskrypcji push:', err);
                    });
                } else {
                    console.warn('[NotificationsPanel] 📱 ⚠️ PushManager nie jest dostępny');
                }
            }).catch(err => {
                console.error('[NotificationsPanel] 📱 ❌ Błąd sprawdzania service workera:', err);
            });
        }
    }, [unreadCount, onUnreadCountChange]);

    // Zamknij panel po kliknięciu poza nim
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (panelRef.current && !panelRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isOpen, onClose]);

    useEffect(() => {
        if (!isOpen) return;
        const handleEsc = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    // Funkcja do ładowania powiadomień (dostępna globalnie)
    const loadNotifications = useCallback(async () => {
        const ids = allUserIds.length > 0 ? allUserIds : (resolvedUserId ? [resolvedUserId] : []);
        if (!user || ids.length === 0) {
            setNotifications([]);
            return;
        }

        if (quotaError) {
            return;
        }

        console.log('[NotificationsPanel] 📥 Ładuję powiadomienia dla:', ids);

        try {
            const idsParam = ids.join(',');
            const response = await fetch(`${getApiBase()}/api/getNotifications?userId=${encodeURIComponent(idsParam)}`);
            if (response.status === 429) {
                console.warn('[NotificationsPanel] ⚠️ Przekroczono limit Firestore');
                setQuotaError(true);
                setNotifications([]);
                if (onUnreadCountChange) {
                    onUnreadCountChange(0);
                }
                return;
            }

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`[NotificationsPanel] ❌ Błąd HTTP ${response.status}:`, errorText);
                throw new Error(`HTTP ${response.status}`);
            }
            
            const data = await response.json();
            if (quotaError) {
                setQuotaError(false);
            }
            const notifs = data.notifications || [];
            console.log(`[NotificationsPanel] ✅ Załadowano ${notifs.length} powiadomień (${notifs.filter(n => !n.read).length} nieprzeczytanych)`);
            setNotifications(notifs);
            
            // Przywróć stan odpowiedzi na propozycje z bazy danych
            const responsesFromDb = {};
            notifs.forEach(notif => {
                if (notif.type === 'propose_staff' && notif.proposalResponse) {
                    responsesFromDb[notif.id] = notif.proposalResponse;
                }
            });
            setProposalResponses(responsesFromDb);
            
            // Aktualizuj licznik nieprzeczytanych powiadomień po załadowaniu
            const unreadCount = notifs.filter(n => !n.read).length;
            if (onUnreadCountChange) {
                onUnreadCountChange(unreadCount);
            }
        } catch (error) {
            console.error('[NotificationsPanel] Błąd ładowania powiadomień:', error);
            if (error?.message?.includes('firestore-quota') || error?.message?.includes('429')) {
                setQuotaError(true);
            }
            setNotifications([]);
            if (onUnreadCountChange) {
                onUnreadCountChange(0);
            }
        } finally {
            setLoading(false);
        }
    }, [user, resolvedUserId, allUserIds, onUnreadCountChange, quotaError]);

    // Zapisz ref do loadNotifications i powiadom parent
    useEffect(() => {
        loadNotificationsRef.current = loadNotifications;
        // Powiadom parent, że funkcja jest gotowa do użycia
        if (onLoadNotificationsReady) {
            onLoadNotificationsReady(loadNotifications);
        }
    }, [loadNotifications, onLoadNotificationsReady]);

    // Jedno getNotifications przy starcie sesji. Bez interwału.
    // Kolejny odczyt wymusza push (listener Service Workera poniżej) albo odświeżenie harmonogramu.
    useEffect(() => {
        if (!user || !resolvedUserId) {
            if (typeof navigator !== 'undefined' && 'clearAppBadge' in navigator) {
                navigator.clearAppBadge().catch(() => {});
            }
            return;
        }
        if (loadNotificationsRef.current) {
            loadNotificationsRef.current();
        }
    }, [user, resolvedUserId]);

    // Przeczytane tylko po kliknięciu w konkretne powiadomienie — nie przy samym otwarciu panelu.

    // Nasłuchuj na wiadomości z Service Workera (push notifications)
    useEffect(() => {
        if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;

        const handleMessage = (event) => {
            const { type, data } = event.data || {};
            
            // Ustaw badge jeśli otrzymano liczbę nieprzeczytanych powiadomień
            // (Service worker już ustawił badge, ale aktualizujemy go tutaj dla pewności)
            if (data?.badgeCount !== undefined && typeof navigator !== 'undefined' && 'setAppBadge' in navigator) {
                const badgeCount = Math.max(0, parseInt(data.badgeCount, 10) || 0);
                if (badgeCount > 0) {
                    navigator.setAppBadge(badgeCount).then(() => {
                        console.log(`[NotificationsPanel] ✅ Badge zaktualizowany z push: ${badgeCount}`);
                    }).catch(err => {
                        console.warn('[NotificationsPanel] ⚠️ Nie udało się ustawić badge z push:', err);
                    });
                } else {
                    navigator.clearAppBadge().then(() => {
                        console.log('[NotificationsPanel] ✅ Badge wyczyszczony z push');
                    }).catch(err => {
                        console.warn('[NotificationsPanel] ⚠️ Nie udało się wyczyścić badge z push:', err);
                    });
                }
            }
            
            // Odśwież powiadomienia po otrzymaniu push notification (najpierw, aby mieć aktualne dane)
            if (type === 'PUSH_NOTIFICATION_RECEIVED' || type === 'DATA_VERSION_UPDATE' || type === 'PROPOSAL_ACCEPTED' || type === 'PROPOSAL_REJECTED') {
                console.log('[NotificationsPanel] 📨 Otrzymano push notification, odświeżam powiadomienia...');
                // Odśwież powiadomienia natychmiast (nie czekaj)
                if (loadNotificationsRef.current) {
                    loadNotificationsRef.current();
                }
            }
            
            // Ustaw badge jeśli otrzymano liczbę nieprzeczytanych powiadomień
            // (po odświeżeniu powiadomień, aby mieć aktualną liczbę)
            // Na Androidzie badge może być ustawiany automatycznie przez system, ale ustawiamy go też programowo
            if (data?.badgeCount !== undefined && typeof navigator !== 'undefined' && 'setAppBadge' in navigator) {
                const badgeCount = Math.max(0, parseInt(data.badgeCount, 10) || 0);
                if (badgeCount > 0) {
                    navigator.setAppBadge(badgeCount).then(() => {
                        console.log(`[NotificationsPanel] ✅ Badge ustawiony z push na ${badgeCount}`);
                    }).catch(err => {
                        console.warn('[NotificationsPanel] ⚠️ Nie udało się ustawić badge z push:', err);
                    });
                } else {
                    navigator.clearAppBadge().then(() => {
                        console.log('[NotificationsPanel] ✅ Badge wyczyszczony z push');
                    }).catch(err => {
                        console.warn('[NotificationsPanel] ⚠️ Nie udało się wyczyścić badge z push:', err);
                    });
                }
            } else if (data?.badgeCount !== undefined) {
                // Jeśli Badge API nie jest dostępne, spróbuj ustawić badge przez service worker
                // (niektóre przeglądarki na Androidzie mogą wymagać tego)
                console.log('[NotificationsPanel] ℹ️ Badge API nie jest dostępne, ale badge może być ustawiony automatycznie przez system na Androidzie');
            }
        };

        navigator.serviceWorker.addEventListener('message', handleMessage);

        return () => {
            navigator.serviceWorker.removeEventListener('message', handleMessage);
        };
    }, []);

    // Sprawdź czy powinien pokazać popup z prośbą o uprawnienia
    useEffect(() => {
        if (!isOpen || !user || !pushSubscription.isSupported) return;
        
        // Sprawdź czy użytkownik nie ma włączonych powiadomień i nie odrzucił ich wcześniej
        const permission = Notification.permission;
        const shouldShowPrompt = 
            permission === 'default' && 
            !pushSubscription.isSubscribed &&
            !localStorage.getItem('notificationPromptDismissed');
        
        if (shouldShowPrompt) {
            // Opóźnij pokazanie popupu o 1 sekundę, aby nie przeszkadzać od razu
            const timer = setTimeout(() => {
                setShowPermissionPrompt(true);
            }, 1000);
            
            return () => clearTimeout(timer);
        }
    }, [isOpen, user, pushSubscription.isSupported, pushSubscription.isSubscribed]);

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'add_staff':
                return <MdCheckCircle className="text-green-500" size={20} />;
            case 'remove_staff':
                return <MdCancel className="text-red-500" size={20} />;
            case 'propose_staff':
                return <MdNotifications className="text-yellow-500" size={20} />;
            case 'proposal_accepted':
                return <MdCheckCircle className="text-green-500" size={20} />;
            case 'proposal_rejected':
                return <MdCancel className="text-red-500" size={20} />;
            default:
                return <MdInfo className="text-blue-500" size={20} />;
        }
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return 'Brak daty';
        let ms = null;
        if (dateValue && typeof dateValue === 'object' && typeof dateValue.toDate === 'function') {
            ms = dateValue.toDate().getTime();
        } else if (dateValue && typeof dateValue === 'object' && (dateValue.seconds != null || dateValue._seconds != null)) {
            ms = (dateValue.seconds ?? dateValue._seconds ?? 0) * 1000;
        } else if (typeof dateValue === 'string') {
            ms = Date.parse(dateValue);
        } else if (dateValue instanceof Date) {
            ms = dateValue.getTime();
        } else if (typeof dateValue === 'number') {
            ms = dateValue;
        }
        if (ms != null && Number.isFinite(ms)) {
            return new Date(ms).toLocaleDateString('pl-PL', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
            });
        }
        return 'Brak daty';
    };

    const formatTimestamp = (timestampValue) => {
        if (!timestampValue) return 'Brak czasu';
        
        let date = null;
        
        // Obsługa Firestore Timestamp
        if (timestampValue && typeof timestampValue === 'object') {
            if (timestampValue.toDate && typeof timestampValue.toDate === 'function') {
                date = timestampValue.toDate();
            } else if (timestampValue.seconds) {
                // Firestore Timestamp jako obiekt z seconds
                date = new Date(timestampValue.seconds * 1000);
            } else if (timestampValue._seconds) {
                // Firestore Timestamp jako obiekt z _seconds
                date = new Date(timestampValue._seconds * 1000);
            }
        }
        
        // Obsługa liczby (milliseconds)
        if (!date && typeof timestampValue === 'number') {
            date = new Date(timestampValue);
        }
        
        // Obsługa stringów
        if (!date && typeof timestampValue === 'string') {
            date = new Date(timestampValue);
        }
        
        if (date && !isNaN(date.getTime())) {
            return date.toLocaleString('pl-PL', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
        
        return 'Brak czasu';
    };

    const markAsRead = async (notificationId) => {
        try {
            const response = await fetch(`${getApiBase()}/api/markNotificationRead`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ notificationId }),
            });
            
            if (response.ok) {
                setNotifications(prev => prev.map(n => 
                    n.id === notificationId ? { ...n, read: true } : n
                ));
            }
        } catch (error) {
            console.error('[NotificationsPanel] Błąd oznaczania jako przeczytane:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            const unreadNotifications = notifications.filter(n => !n.read);
            if (unreadNotifications.length === 0) return;
            
            // Oznacz wszystkie nieprzeczytane powiadomienia jako przeczytane
            const promises = unreadNotifications.map(notif => 
                fetch(`${getApiBase()}/api/markNotificationRead`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ notificationId: notif.id }),
                })
            );
            
            await Promise.allSettled(promises);
            
            // Zaktualizuj lokalny stan
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        } catch (error) {
            console.error('[NotificationsPanel] Błąd oznaczania wszystkich jako przeczytane:', error);
        }
    };

    const deleteNotification = async (notificationId) => {
        try {
            const response = await fetch(`${getApiBase()}/api/deleteNotification`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ notificationId }),
            });
            
            if (response.ok) {
                setNotifications(prev => prev.filter(n => n.id !== notificationId));
            }
        } catch (error) {
            console.error('[NotificationsPanel] Błąd usuwania powiadomienia:', error);
        }
    };

    const handleProposalResponse = async (notificationId, action) => {
        try {
            const notif = notifications.find(n => n.id === notificationId);
            if (!notif) return;
            
            // Zapisz odpowiedź w stanie (aby wyszarzyć niewybraną opcję)
            setProposalResponses(prev => ({ ...prev, [notificationId]: action }));
            
            // Oznacz powiadomienie jako przeczytane
            await markAsRead(notificationId);
            
            const response = await fetch(`${getApiBase()}/api/handleProposalResponse`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(clientInstanceId ? { 'x-client-id': clientInstanceId } : {}),
                },
                body: JSON.stringify({
                    action,
                    staffName: notif.staffName || user?.name || user?.login,
                    eventName: notif.eventName,
                    date: notif.date,
                    eventId: notif.eventId,
                    calendarId: notif.calendarId,
                    requestedBy: notif.requestedBy,
                    notificationId: notif.id,
                    clientInstanceId,
                }),
            });
            
            if (response.ok) {
                // Zaktualizuj lokalny stan powiadomienia z zapisanym statusem odpowiedzi
                setNotifications(prev => prev.map(n => 
                    n.id === notificationId 
                        ? { ...n, proposalResponse: action, read: true }
                        : n
                ));
                
                // Odśwież harmonogram po krótkim opóźnieniu (aby dać czas na zapis do bazy)
                if (onRefreshSchedule) {
                    setTimeout(() => {
                        onRefreshSchedule();
                    }, 1000);
                } else {
                    setTimeout(() => {
                        window.location.reload();
                    }, 1000);
                }
            }
        } catch (error) {
            console.error('[NotificationsPanel] Błąd obsługi odpowiedzi na propozycję:', error);
        }
    };

    const getNotificationText = (notification) => {
        if (!notification || typeof notification !== 'object') return 'Powiadomienie';
        const title = notification.title != null ? String(notification.title).trim() : '';
        const body = notification.body != null ? String(notification.body).trim() : '';
        if (title) return body ? `${title} — ${body}` : title;
        if (body) return body;
        const type = notification.type;
        const eventName = notification.eventName || '—';
        const dateStr = formatDate(notification.date);
        const userName = notification.userName || 'Ktoś';
        switch (type) {
            case 'add_staff':
                return `Zostałeś dodany do wydarzenia: ${eventName} (${dateStr})`;
            case 'remove_staff':
                return `Zostałeś usunięty z wydarzenia: ${eventName} (${dateStr})`;
            case 'propose_staff':
                return `Propozycja udziału w wydarzeniu: ${eventName} (${dateStr})`;
            case 'proposal_accepted':
                return `${userName} zaakceptował propozycję udziału w wydarzeniu: ${eventName} (${dateStr})`;
            case 'proposal_rejected':
                return `${userName} odrzucił propozycję udziału w wydarzeniu: ${eventName} (${dateStr})`;
            default:
                return (notification.message && String(notification.message).trim()) || 'Powiadomienie';
        }
    };

        if (!isOpen) return null;

        return (
            <>
            <div
                className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
                onClick={onClose}
                aria-hidden="true"
            />
            <div
                ref={panelRef}
                className="absolute right-0 mt-2 w-96 max-w-[90vw] rounded-lg shadow-lg z-50 border
                     bg-white dark:bg-gray-800
                     border-gray-200 dark:border-gray-700
                     max-h-[80vh] flex flex-col"
        >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b
                          border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2">
                    <MdNotifications size={24} className="text-blue-500" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Aktualności
                    </h2>
                </div>
                <button
                    onClick={onClose}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    title="Zamknij"
                >
                    <MdClose size={20} />
                </button>
            </div>

            {/* Ustawienia powiadomień push */}
            {pushSubscription.isSupported && (
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700
                              bg-gray-50 dark:bg-gray-900">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            {pushSubscription.isSubscribed ? (
                                <MdNotificationsActive className="text-green-500" size={20} />
                            ) : (
                                <MdNotificationsOff className="text-gray-400" size={20} />
                            )}
                            <div className="flex flex-col">
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    Powiadomienia push
                                </span>
                                {pushSubscription.isSubscribed && pushSubscription.subscription && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        ✅ Subskrypcja aktywna
                                    </span>
                                )}
                                {!pushSubscription.isSubscribed && pushSubscription.permission === 'granted' && (
                                    <span className="text-xs text-yellow-500">
                                        ⚠️ Zgoda udzielona, ale brak subskrypcji
                                    </span>
                                )}
                            </div>
                        </div>
                            {pushSubscription.syncError && (
                                <p className="text-xs mt-1 text-amber-600 dark:text-amber-400">
                                    {pushSubscription.syncError}
                                </p>
                            )}
                            {pushSubscription.permission === 'denied' ? (
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-red-500">
                                    Zablokowane
                                </span>
                                <button
                                    onClick={() => {
                                        alert('Aby odblokować powiadomienia:\n\n' +
                                            '1. Kliknij ikonę kłódki w pasku adresu przeglądarki\n' +
                                            '2. Znajdź opcję "Powiadomienia"\n' +
                                            '3. Zmień na "Zezwól"\n' +
                                            '4. Odśwież stronę');
                                    }}
                                    className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded
                                             hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300
                                             dark:hover:bg-gray-600
                                             flex items-center gap-1"
                                    title="Jak odblokować powiadomienia"
                                >
                                    <MdSettings size={14} />
                                    Instrukcja
                                </button>
                            </div>
                        ) : pushSubscription.isSubscribed ? (
                            <button
                                onClick={async () => {
                                    await pushSubscription.unsubscribe();
                                }}
                                disabled={pushSubscription.isSubscribing}
                                className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded
                                         hover:bg-red-200 dark:bg-red-900 dark:text-red-200
                                         dark:hover:bg-red-800"
                            >
                                Wyłącz
                            </button>
                        ) : (
                            <button
                                onClick={async () => {
                                    const result = await pushSubscription.subscribe();
                                    if (result) {
                                        setShowPermissionPrompt(false);
                                        localStorage.setItem('notificationPromptDismissed', 'true');
                                    }
                                }}
                                disabled={pushSubscription.isSubscribing}
                                className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded
                                         hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200
                                         dark:hover:bg-blue-800
                                         disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {pushSubscription.isSubscribing ? 'Włączanie...' : 'Włącz'}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Porady dla Androida – z przyciskiem otwierającym ustawienia */}
            {pushSubscription.isSupported && (
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                    <button
                        type="button"
                        onClick={() => setShowAndroidTips(v => !v)}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline focus:outline-none"
                    >
                        {showAndroidTips ? 'Ukryj porady' : 'Na Androidzie nie widzisz powiadomień?'}
                    </button>
                    {showAndroidTips && (
                        <>
                            <ul className="text-xs mt-2 pl-4 list-disc text-gray-600 dark:text-gray-400 space-y-1">
                                <li><strong>Zainstaluj aplikację</strong> – w Chrome: menu (⋮) → „Dodaj do ekranu głównego” / „Zainstaluj aplikację”.</li>
                                <li><strong>Powiadomienia i dymki</strong> – w ustawieniach aplikacji włącz <strong>Powiadomienia</strong> oraz <strong>Dymki</strong>. Poniżej możesz otworzyć ustawienia systemu – znajdź tę aplikację i włącz powiadomienia.</li>
                                <li><strong>Oszczędzanie baterii</strong> – wyłącz optymalizację baterii dla tej aplikacji.</li>
                            </ul>
                            {typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent) && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const fallback = window.location.origin || '';
                                        const intent = `intent://#Intent;package=com.android.settings;action=android.intent.action.MAIN;S.browser_fallback_url=${encodeURIComponent(fallback)};end`;
                                        window.location.href = intent;
                                    }}
                                    className="mt-2 w-full text-xs px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none"
                                >
                                    Otwórz ustawienia – potem wybierz tę aplikację i włącz Powiadomienia oraz Dymki
                                </button>
                            )}
                        </>
                    )}
                </div>
            )}

            {quotaError && (
                <div className="px-4 py-3 text-sm text-red-700 bg-red-50 border-b border-red-200 dark:bg-red-900 dark:text-red-100 dark:border-red-800">
                    Przekroczono dzienny limit Firestore. Powiadomienia wrócą po odnowieniu limitu (zwykle po północy).
                </div>
            )}

            {/* Popup z prośbą o uprawnienia */}
            {showPermissionPrompt && (
                <NotificationPermissionPrompt
                    onClose={() => {
                        setShowPermissionPrompt(false);
                        localStorage.setItem('notificationPromptDismissed', 'true');
                    }}
                    onGrant={async () => {
                        const result = await pushSubscription.subscribe();
                        if (result) {
                            localStorage.setItem('notificationPromptDismissed', 'true');
                        }
                        return result;
                    }}
                />
            )}

            {/* Lista powiadomień */}
            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <div className="p-8 text-center">
                        <div className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-transparent
                                     dark:border-gray-600 dark:border-t-transparent animate-spin mx-auto mb-2" />
                        <p className="text-sm text-gray-600 dark:text-gray-400">Ładowanie...</p>
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="p-8 text-center">
                        <MdNotifications size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Brak powiadomień
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            Powiadomienia pojawią się tutaj, gdy zostaniesz dodany lub usunięty z wydarzenia
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                        {notifications.map(notif => (
                            <div
                                key={notif.id}
                                className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                    <div className="flex items-start gap-3">
                                        <div className="mt-0.5">
                                            {getNotificationIcon(notif.type)}
                                        </div>
                                        <div className="flex-1 min-w-0 overflow-hidden">
                                            <div className="text-sm text-gray-900 dark:text-gray-100 min-h-[1.5em] break-words">
                                                {(() => {
                                                    const title = (notif.title != null && String(notif.title).trim()) || (notif.Title != null && String(notif.Title).trim()) || '';
                                                    const body = (notif.body != null && String(notif.body).trim()) || (notif.Body != null && String(notif.Body).trim()) || '';
                                                    if (title && body) {
                                                        return (
                                                            <>
                                                                <div className="font-medium">{title}</div>
                                                                <div className="mt-0.5 text-gray-600 dark:text-gray-300">{body}</div>
                                                            </>
                                                        );
                                                    }
                                                    if (title) return <div className="font-medium">{title}</div>;
                                                    if (body) return <div>{body}</div>;
                                                    const fallback = getNotificationText(notif);
                                                    return <div>{fallback && String(fallback).trim() ? fallback : 'Powiadomienie'}</div>;
                                                })()}
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                {formatTimestamp(notif.createdAt || notif.timestamp)}
                                            </div>
                                            {/* Przyciski akcji dla propozycji */}
                                            {notif.type === 'propose_staff' && (
                                                <div className="flex gap-2 mt-2">
                                                    {(() => {
                                                        // Użyj zapisanego statusu z bazy lub z lokalnego stanu
                                                        const responseStatus = notif.proposalResponse || proposalResponses[notif.id];
                                                        const isAccepted = responseStatus === 'accept';
                                                        const isRejected = responseStatus === 'reject';
                                                        const isResponded = isAccepted || isRejected;
                                                        
                                                        return (
                                                            <>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (!isResponded) {
                                                                            handleProposalResponse(notif.id, 'accept');
                                                                        }
                                                                    }}
                                                                    disabled={isResponded}
                                                                    className={`px-3 py-1 text-xs rounded flex items-center gap-1
                                                                        ${isAccepted
                                                                            ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200'
                                                                            : isRejected
                                                                            ? 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400 opacity-50 cursor-not-allowed'
                                                                            : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800'
                                                                        }`}
                                                                    title={isRejected ? 'Opcja niedostępna - wybrano inną opcję' : isAccepted ? 'Zaakceptowano' : 'Akceptuj propozycję'}
                                                                >
                                                                    <MdThumbUp size={16} />
                                                                    {isAccepted ? 'Zaakceptowano' : 'Akceptuj'}
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (!isResponded) {
                                                                            handleProposalResponse(notif.id, 'reject');
                                                                        }
                                                                    }}
                                                                    disabled={isResponded}
                                                                    className={`px-3 py-1 text-xs rounded flex items-center gap-1
                                                                        ${isRejected
                                                                            ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200'
                                                                            : isAccepted
                                                                            ? 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400 opacity-50 cursor-not-allowed'
                                                                            : 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800'
                                                                        }`}
                                                                    title={isAccepted ? 'Opcja niedostępna - wybrano inną opcję' : isRejected ? 'Odrzucono' : 'Odrzuć propozycję'}
                                                                >
                                                                    <MdThumbDown size={16} />
                                                                    {isRejected ? 'Odrzucono' : 'Odrzuć'}
                                                                </button>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                deleteNotification(notif.id);
                                            }}
                                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded
                                                     text-red-600 dark:text-red-400"
                                            title="Usuń"
                                        >
                                            <MdDelete size={18} />
                                        </button>
                                    </div>
                                </div>
                        ))}
                    </div>
                )}
            </div>
            </div>
            </>
    );
}

